import { Link } from 'react-router';
import { CalendarRange, Network, PlugZap, Sparkles, Stethoscope, Workflow } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';
import { EnterpriseProofPack } from '../components/enterprise/EnterpriseProofPack';
import { EnterpriseSalesNav } from '../components/enterprise/EnterpriseSalesNav';
import { useSEO } from '../lib/seo';

const platformBlocks = [
  {
    icon: Network,
    title: 'Partner API',
    text: 'Org-level analytics endpoints designed for partner platforms with scoped credentials, boundary controls, and deployment-safe contracts.',
  },
  {
    icon: PlugZap,
    title: 'Integration registry',
    text: 'Structured integration onboarding for Slack, Teams, calendar context, HRIS, and care workflow tooling.',
  },
  {
    icon: CalendarRange,
    title: 'Context enrichment',
    text: 'Use schedule and lifecycle signals to improve intervention timing, precision, and forecast relevance.',
  },
  {
    icon: Stethoscope,
    title: 'Therapist pathway',
    text: 'Clinician-facing pathway for stronger continuity between sessions and trend-informed preparation.',
  },
];

export default function EnterprisePlatform() {
  useSEO({
    title: 'Enterprise Platform & Integrations | TENSION for Work',
    description:
      'Explore TENSION enterprise platform capabilities: partner API, integration layer, and therapist pathway concepts.',
    canonical: '/enterprise/platform',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    keywords: ['enterprise api', 'relationship analytics api', 'hris integration', 'slack teams integration'],
  });

  return (
    <WarmMarketingBackdrop>
      <EnterpriseSalesNav variant="light" />
      <div className="enterprise-public-shell mx-auto min-h-screen max-w-6xl space-y-6 px-4 py-10 md:py-14">
        <header className="enterprise-hero-shell">
          <div className="enterprise-hero-overlay" />
          <div className="enterprise-hero-content">
          <img
            src="/branding/tension-enterprise-logo.svg"
            alt="TENSION Enterprise"
            className="mb-4 h-auto w-full max-w-md rounded-xl border border-slate-700/70 bg-slate-950/70 p-1"
          />
          <p className="inline-flex rounded-full bg-violet-100 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-violet-700">
            Platform layer
          </p>
          <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-900 md:text-5xl">
            Platform capabilities for enterprise-scale deployment
          </h1>
          <p className="mt-3 max-w-3xl text-slate-700 md:text-lg">
            Beyond UX, TENSION for Work is architected to integrate into employer, benefits, and care ecosystems with
            enterprise-grade controls and operational consistency.
          </p>
          </div>
        </header>

        <div className="grid gap-4 md:grid-cols-2">
          {platformBlocks.map((block) => (
            <Card key={block.title} className="enterprise-surface-card enterprise-lift-card p-6">
              <div className="enterprise-icon-chip">
                <block.icon className="h-7 w-7 text-violet-600" aria-hidden />
              </div>
              <h2 className="mt-3 text-lg font-semibold text-slate-900">{block.title}</h2>
              <p className="mt-2 text-sm text-slate-700">{block.text}</p>
            </Card>
          ))}
        </div>

        <section className="enterprise-surface-card rounded-2xl border-fuchsia-100 p-6 md:p-8">
          <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900 md:text-2xl">
            <Sparkles className="h-5 w-5 text-fuchsia-600" aria-hidden />
            Implementation status
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-700 md:text-base">
            Enterprise API routes, integration registration, and pilot event pipelines are in place. Production onboarding
            extends this with key lifecycle controls, partner governance, and tenant-specific security boundaries.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/enterprise/identity">
              <Button variant="outline" className="border-violet-200 bg-white text-violet-800">
                Identity (SSO scaffold)
              </Button>
            </Link>
            <Link to="/enterprise/whitelabel">
              <Button variant="outline" className="border-violet-200 bg-white text-violet-800">
                White-label profile
              </Button>
            </Link>
            <Link to="/enterprise/partner-docs">
              <Button variant="outline" className="border-violet-200 bg-white text-violet-800">
                Partner docs
              </Button>
            </Link>
            <Link to="/enterprise/expansion">
              <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:opacity-95">
                View expansion programs
              </Button>
            </Link>
            <Link to="/business-inquiries?inquiryType=enterprise">
              <Button variant="outline" className="border-violet-200 bg-white text-violet-800">
                Discuss integrations
              </Button>
            </Link>
          </div>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-violet-50 px-3 py-1 text-xs text-violet-700">
            <Workflow className="h-3.5 w-3.5" aria-hidden />
            Platform roadmap includes research and therapist channels.
          </div>
        </section>

        <EnterpriseProofPack compact />
      </div>
    </WarmMarketingBackdrop>
  );
}
